import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { toast } from "react-toastify";
import { useAppSelector } from "../../store/hooks";
import { useAddArtistArtworkMutation } from "../../services/rtkApi/artistsGalleryApi";
import ArtworkForm, { ArtworkFormValues } from "./ArtworkForm";
import { ArtistsGalleryFontLinks } from "./ArtistsGalleryFontLinks";
import { artistsGalleryFontFamily } from "./artistsGalleryFont";

const AddArtistArtworkPage: React.FC = () => {
  const token = useAppSelector((state) => state.auth.token);
  const username = useAppSelector((state) => state.auth.username);
  const bootstrapStatus = useAppSelector((state) => state.auth.bootstrapStatus);
  const navigate = useNavigate();
  const [addArtistArtwork, { isLoading }] = useAddArtistArtworkMutation();

  // Gate purely on "do we have a session?" — leave role enforcement to the
  // backend (auth + canContribute middleware on POST /artists-gallery/upload).
  // Mirrors the existing JWT-role inconsistencies: the local `role` field
  // doesn't always reflect newly-granted roles, and frontend gating was
  // bouncing already-signed-in admins.
  const ready = bootstrapStatus === "ready";
  const signedIn = !!token;

  const handleSubmit = async (values: ArtworkFormValues) => {
    if (!values.image) {
      toast.error("Please pick an image", { position: "bottom-right" });
      return;
    }
    const fd = new FormData();
    fd.append("title", values.title);
    fd.append("description", values.description);
    if (values.dateMade) {
      fd.append("dateMade", new Date(values.dateMade).toISOString());
    }
    fd.append("donation", String(values.donation));
    fd.append("image", values.image);
    try {
      await addArtistArtwork(fd).unwrap();
      navigate(username ? `/art/${encodeURIComponent(username)}` : "/art");
    } catch (err: any) {
      // backend canContribute returns 403 with { message } when role missing
      toast.error(err?.data?.message || "Failed to add artwork", {
        position: "bottom-right",
      });
    }
  };

  return (
    <Page>
      <ArtistsGalleryFontLinks />
      <Card>
        <Heading>add artwork</Heading>
        <Lede>
          submitted artworks go through admin review before appearing publicly on
          the site.
        </Lede>

        {!ready && <Notice>resolving session…</Notice>}
        {ready && !signedIn && (
          <Gate>
            you need to sign in to submit artwork.
          </Gate>
        )}

        <ArtworkForm
          submitLabel="submit for review"
          isSubmitting={isLoading}
          requireImage
          onSubmit={handleSubmit}
          onCancel={() => navigate(-1)}
        />
      </Card>
    </Page>
  );
};

const Page = styled.div`
  background-color: #c4ffd2;
  padding: 40px;
  min-height: 100vh;
  font-family: ${artistsGalleryFontFamily};

  @media (max-width: 600px) {
    padding: 20px;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border: 4px solid #000000;
  box-shadow: 5px 10px rgba(0, 0, 0, 0.7);
  padding: 24px;
  max-width: 640px;
`;

const Heading = styled.h2`
  margin: 0 0 0.4rem 0;
  font-weight: 700;
`;

const Lede = styled.p`
  margin: 0 0 1.2rem 0;
  font-weight: 500;
  font-size: 0.9rem;
  opacity: 0.75;
`;

const Notice = styled.div`
  background: #fff77a;
  border: 3px solid #000000;
  box-shadow: 3px 4px rgba(0, 0, 0, 0.7);
  padding: 8px 12px;
  margin-bottom: 16px;
  font-weight: 600;
  font-size: 0.9rem;
`;

const Gate = styled.div`
  background: #ffd1f0;
  border: 3px solid #000000;
  box-shadow: 3px 4px rgba(0, 0, 0, 0.7);
  padding: 12px 14px;
  margin-bottom: 16px;
  font-size: 0.9rem;
  line-height: 1.4;

  code {
    background: #000000;
    color: #ffffff;
    padding: 1px 6px;
    font-size: 0.85rem;
  }
`;

export default AddArtistArtworkPage;
